// Admin money & usage API — typed wrappers around /admin/v1/money/*.

import type { ApiClient } from '../api-client.js';

export type Granularity = 'day' | 'hour';

export interface ByProviderBucket {
  ts: string;
  provider: string;
  model: string | null;
  inputTokens: number;
  outputTokens: number;
  callCount: number;
  costUsd: number;
}

export interface TopTenantRow {
  tenantId: string;
  tenantName: string;
  planCode: string | null;
  costUsd: number;
  callCount: number;
  inputTokens: number;
  outputTokens: number;
}

export interface StorageRow {
  tenantId: string;
  tenantName: string;
  planCode: string | null;
  bytes: number;
  docCount: number;
  capBytes: number | null;
  utilizationPct: number | null;
  estimatedMonthlyCostUsd: number;
}

export interface AnomalyFreezeRow {
  tenantId: string;
  tenantName: string;
  frozenAt: string;
  frozenReason: string;
}
export interface AnomalyTokenSpikeRow {
  conversationId: string;
  tenantId: string;
  tenantName: string;
  totalTokens: number;
  baselineP99: number;
  ratio: number;
  lastTs: string;
}
export interface AnomalyKeySpikeRow {
  apiKeyId: string;
  apiKeyPrefix: string;
  tenantId: string;
  tenantName: string;
  hourlyCostUsd: number;
  baselineHourlyUsd: number;
  ratio: number;
}
export interface Anomalies {
  recentFreezes: AnomalyFreezeRow[];
  tokenSpikes: AnomalyTokenSpikeRow[];
  keySpikes: AnomalyKeySpikeRow[];
}

export const MONEY_BY_PROVIDER_QUERY_KEY = ['admin', 'money', 'by-provider'] as const;
export const MONEY_TOP_TENANTS_QUERY_KEY = ['admin', 'money', 'top-tenants'] as const;
export const MONEY_STORAGE_QUERY_KEY = ['admin', 'money', 'storage'] as const;
export const MONEY_ANOMALIES_QUERY_KEY = ['admin', 'money', 'anomalies'] as const;

export const moneyApi = {
  async byProvider(
    client: ApiClient,
    params: { from: Date; to: Date; granularity?: Granularity },
  ): Promise<{ buckets: ByProviderBucket[] }> {
    const sp = new URLSearchParams({
      from: params.from.toISOString(),
      to: params.to.toISOString(),
    });
    if (params.granularity) sp.set('granularity', params.granularity);
    return client.get<{ buckets: ByProviderBucket[] }>(`/money/by-provider?${sp.toString()}`);
  },
  async topTenants(
    client: ApiClient,
    params: { from: Date; to: Date; provider?: string; limit?: number },
  ): Promise<{ rows: TopTenantRow[] }> {
    const sp = new URLSearchParams({
      from: params.from.toISOString(),
      to: params.to.toISOString(),
    });
    if (params.provider) sp.set('provider', params.provider);
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    return client.get<{ rows: TopTenantRow[] }>(`/money/top-tenants?${sp.toString()}`);
  },
  async storage(
    client: ApiClient,
    params: { limit?: number; overCapOnly?: boolean } = {},
  ): Promise<{ rows: StorageRow[] }> {
    const sp = new URLSearchParams();
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    if (params.overCapOnly !== undefined) sp.set('overCapOnly', String(params.overCapOnly));
    const tail = sp.toString();
    return client.get<{ rows: StorageRow[] }>(
      `/money/storage${tail.length > 0 ? `?${tail}` : ''}`,
    );
  },
  async anomalies(client: ApiClient): Promise<Anomalies> {
    return client.get<Anomalies>('/money/anomalies');
  },
};
